import React, { useState } from 'react';
import { createTheme, ThemeProvider, CssBaseline, Fab, Tooltip, Zoom, SpeedDial, SpeedDialIcon, SpeedDialAction } from '@mui/material';
import { ruRU } from '@mui/material/locale';
import './App.css';
import { motion } from 'framer-motion';

// Import icons
import ChatIcon from '@mui/icons-material/Chat';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import HelpIcon from '@mui/icons-material/Help';
import TelegramIcon from '@mui/icons-material/Telegram';

// Import components
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import ProjectDescription from './components/ProjectDescription';
import TestimonialsSection from './components/TestimonialsSection';
import FaqSection from './components/FaqSection';
import Footer from './components/Footer';

// Use motion.div for wrapping Material UI components
const MotionFab = motion(Fab);

const MAINTENANCE_MODE = true;

const App = () => {
  // State for dark/light mode
  const [darkMode, setDarkMode] = useState(false);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);

  // Speed dial actions
  const actions = [
    { icon: <TelegramIcon />, name: 'Связаться в Telegram', action: () => window.open('https://t.me/MoodModSupport', '_blank') },
    { icon: <HelpIcon />, name: 'Помощь', action: () => document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' }) },
  ];

  // Create a custom theme based on dark/light mode
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#6573c3' : '#3f51b5',
        light: darkMode ? '#8c9eff' : '#757de8',
        dark: darkMode ? '#394194' : '#002984',
      },
      secondary: {
        main: darkMode ? '#ff4081' : '#f50057',
        light: darkMode ? '#ff79b0' : '#ff5983',
        dark: darkMode ? '#c60055' : '#bb002f',
      },
      background: {
        default: darkMode ? '#121212' : '#ffffff',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: [
        '"Roboto"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 600,
      },
      h3: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 24px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
    },
  }, ruRU); // Adding Russian locale

  // Toggle dark/light mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  if (MAINTENANCE_MODE) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Roboto, Arial, sans-serif',
        fontSize: '1.5rem',
        textAlign: 'center',
        padding: '0 20px',
        color: '#333',
      }}>
        Приносим извинения, сайт на временном техническом обслуживании
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <ProjectDescription />
        <TestimonialsSection />
        <div id="faq-section">
          <FaqSection />
        </div>
        <Footer />

        {/* Dark/Light mode toggle */}
        <Tooltip 
          title={darkMode ? "Светлый режим" : "Темный режим"} 
          placement="left"
          TransitionComponent={Zoom}
        >
          <motion.div 
            style={{ position: 'fixed', bottom: 110, right: 30, zIndex: 999 }}
            whileHover={{ rotate: 180 }}
            whileTap={{ scale: 0.9 }}
          >
            <Fab
              color="primary"
              aria-label="theme"
              onClick={toggleDarkMode}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </Fab>
          </motion.div>
        </Tooltip>

        {/* Speed Dial for actions */}
        <motion.div
          className="floating-action-button"
          whileHover={{ scale: 1.1 }}
        >
          <SpeedDial
            ariaLabel="Быстрые действия"
            icon={<SpeedDialIcon openIcon={<ChatIcon />} />}
            onClose={() => setOpenSpeedDial(false)}
            onOpen={() => setOpenSpeedDial(true)}
            open={openSpeedDial}
            direction="up"
            FabProps={{
              sx: { 
                bgcolor: 'secondary.main',
                '&:hover': {
                  bgcolor: 'secondary.dark',
                }
              }
            }}
          >
            {actions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                tooltipOpen
                onClick={() => {
                  action.action();
                  setOpenSpeedDial(false);
                }}
              />
            ))}
          </SpeedDial>
        </motion.div>
      </div>
    </ThemeProvider>
  );
}

export default App;
